/**
 * What to give an AI assistant, with a photo, PDF or Word file of a printed report, so it can
 * write the template's JSON. The example is checked by a test against the real schema, so this
 * text cannot drift away from what the server accepts.
 */

/** A complete, valid example that uses every kind of row and range. Ids are left out on purpose. */
export const TEMPLATE_EXAMPLE = {
  name: 'Lipid Profile',
  aliases: ['Lipid Panel', 'Cholesterol Profile'],
  department: 'Biochemistry',
  tags: ['cardiac risk'],
  sampleType: 'Serum (fasting)',
  instrument: 'Fully automated analyzer',
  interpretation: 'Fasting for 10 to 12 hours is advised before the sample is taken.',
  turnaroundDays: 0,
  columns: [
    { role: 'investigation', label: 'Test Name' },
    { role: 'result', label: 'Result' },
    { role: 'unit', label: 'Unit' },
    { role: 'ref_range', label: 'Bio. Ref. Interval' },
  ],
  rows: [
    { kind: 'section', label: 'LIPID PROFILE' },
    {
      kind: 'test',
      code: 'TC',
      label: 'Total Cholesterol',
      valueType: 'numeric',
      unit: 'mg/dL',
      decimals: 0,
      method: 'CHOD-PAP',
      required: true,
      ranges: [
        {
          type: 'bands',
          bands: [
            { label: 'Desirable', max: 200, flag: 'normal' },
            { label: 'Borderline high', min: 200, max: 240, flag: 'high' },
            { label: 'High', min: 240, flag: 'high' },
          ],
          display: '< 200 Desirable',
        },
      ],
    },
    {
      kind: 'test',
      code: 'TG',
      label: 'Triglycerides',
      valueType: 'numeric',
      unit: 'mg/dL',
      decimals: 0,
      method: 'GPO-PAP',
      ranges: [{ type: 'lt', max: 150 }],
      critical: { high: 1000 },
    },
    {
      kind: 'test',
      code: 'HDL',
      label: 'HDL Cholesterol',
      valueType: 'numeric',
      unit: 'mg/dL',
      decimals: 0,
      ranges: [
        { type: 'gte', min: 40, sex: 'M' },
        { type: 'gte', min: 50, sex: 'F' },
      ],
    },
    {
      kind: 'test',
      code: 'LDL',
      label: 'LDL Cholesterol (calculated)',
      valueType: 'numeric',
      unit: 'mg/dL',
      decimals: 0,
      formula: 'TC - HDL - TG / 5',
      ranges: [{ type: 'lt', max: 100 }],
    },
    {
      kind: 'test',
      code: 'RATIO',
      label: 'TC / HDL Ratio',
      valueType: 'numeric',
      decimals: 1,
      formula: 'TC / HDL',
      ranges: [{ type: 'between', min: 0, max: 5 }],
    },
    {
      kind: 'test',
      code: 'APPEAR',
      label: 'Serum Appearance',
      valueType: 'option',
      options: ['Clear', 'Turbid', 'Lipaemic'],
      ranges: [{ type: 'qualitative', expected: ['Clear'] }],
    },
    {
      kind: 'test',
      code: 'REMARK',
      label: 'Remark',
      valueType: 'text',
      ranges: [{ type: 'text' }],
      hideIfEmpty: true,
    },
    {
      kind: 'note',
      text: 'LDL is calculated with the Friedewald formula (valid up to TG 400 mg/dL).',
    },
    {
      kind: 'narrative',
      label: 'Comments',
      text: 'Interpret the result with the patient’s clinical history.',
    },
  ],
  price: 450,
  sensitive: false,
  separateReport: false,
};

export const TEMPLATE_PROMPT = `You are helping me turn a printed laboratory report into a template for LabView, a laboratory reporting system. I will give you a photo, PDF or Word file of the report. Read it and write ONE JSON object that describes the template. Answer with the JSON only: no explanation, no markdown.

WHAT A TEMPLATE IS
A template is the layout of one test or panel (for example "Lipid Profile"): the names of the tests, their units, their reference ranges and any calculated values. It holds NO patient name and NO result values from the page you were given. Use the printed values only to understand the layout and to read the reference ranges.

THE JSON
Top level (all optional except "name" and "rows"):
- "name": the test or panel name as printed, e.g. "Lipid Profile".
- "aliases": other names it is known by, e.g. ["Lipid Panel"].
- "department": e.g. "Haematology", "Biochemistry", "Serology", "Microbiology", "Clinical Pathology", "Coagulation", "Hormones".
- "sampleType": e.g. "Serum", "Whole Blood EDTA", "Urine".
- "instrument", "interpretation" (a short note printed under the table), "tags" (array of words), "turnaroundDays" (whole number), "price" (rupees, only if printed), "sensitive" (true for HIV and similar), "separateReport" (true for cultures).
- "columns": the table columns, in print order. Each is {"role": ..., "label": "..."}. Roles: "investigation" (test name), "result", "unit", "ref_range" (one combined reference column), "ref_min" and "ref_max" (separate columns), "method", "flag", "custom". There must be one "result" column. If the printed report has the usual Test / Result / Unit / Reference columns you may leave "columns" out.
- "rows": the table rows in print order. Each row has a "kind":
  - {"kind": "section", "label": "LIPID PROFILE"} a heading line.
  - {"kind": "test", ...} a test you enter a result for (see below).
  - {"kind": "note", "text": "..."} a printed note line between rows.
  - {"kind": "narrative", "label": "Comments", "text": "..."} a free-text block the technician can edit (histopathology findings, interpretations).

A "test" row:
- "code": a short unique code in CAPITALS, digits and _ only, starting with a letter: HB, TLC, SGPT, TC. Never repeated within the template.
- "label": the name as printed.
- "valueType": "numeric" (default; a number), "text" (free words) or "option" (pick from a list; then "options" is required: ["Negative", "Positive"]). For a list that also lets the person type any other value (a count such as "2-4", or "Nil"), add "allowOther": true.
- "unit": e.g. "g/dL", "mg/dL", "10^3/µL". Copy it as printed.
- "decimals": how many decimals to print (0 to 6).
- "method": e.g. "Colorimetric" (only if printed).
- "formula": for calculated rows, an expression over other rows' codes with + - * / and brackets, e.g. "TC - HDL - TG / 5". Every code used must exist in this template.
- "required": true if a result must always be entered.
- "critical": {"low": 7, "high": 20} when the report states panic values.
- "hideIfEmpty": true to leave the row off the report when no result is entered.
- "footnote": text printed under the row.
- "ranges": the reference ranges. A test can have several, for different sexes or ages; the system picks the one that fits the patient. Each range has a "type":
  - {"type": "between", "min": 13, "max": 17}
  - {"type": "lt", "max": 150} less than; "lte" less than or equal.
  - {"type": "gt", "min": 4} greater than; "gte" greater than or equal.
  - {"type": "bands", "bands": [{"label": "Desirable", "max": 200, "flag": "normal"}, {"label": "High", "min": 240, "flag": "high"}]} for tables of cut-offs. "flag" is "normal", "low", "high" or "abnormal". A value at "min" is included and a value at "max" is excluded.
  - {"type": "qualitative", "expected": ["Negative"]} the normal answers for option or text tests.
  - {"type": "text"} when only free text is expected.
  Any range can also have "sex" ("M", "F" or "O"), "ageMinDays" and "ageMaxDays" (an age window in days: 1 year = 365, 18 years = 6570) and "display" (the exact text to print, if the printed report words it differently, e.g. "< 5.7 % Normal").

RULES
1. Keep every test in the order and with the wording printed. Do not invent tests, ranges or units that are not on the page. If something is not printed, leave that field out.
2. Never put a patient's name, age, ID or a result value into the template.
3. Separate ranges for men and women are separate range objects with "sex" set.
4. Do not write "id" fields; they are added for you.
5. Numbers are JSON numbers (13.5), not strings.
6. If the page holds several different panels, write the one I ask for. If I did not say, write the first and say nothing else.
7. If you cannot read something, leave it out rather than guess.

Here is a complete example of the format:

${JSON.stringify(TEMPLATE_EXAMPLE, null, 2)}

Now read the report I attach and write the JSON.`;
